import styled, { keyframes } from 'styled-components';

interface MessageBubbleProps {
  $role: 'user' | 'model' | 'assistant';
  $isInitial?: boolean;
}

export const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 600px;
  max-height: 80vh;
  background: #f8f9fa;
  overflow: hidden;
`;

export const ChatHeader = styled.div`
  background: linear-gradient(135deg, #428bca 0%, #357abd 100%);
  color: white;
  padding: 15px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  .header-content {
    display: flex;
    align-items: center;
    gap: 15px;

    .lia-avatar {
      width: 50px;
      height: 50px;
      border-radius: 50%;
      object-fit: cover;
      border: 3px solid white;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
      flex-shrink: 0;
    }

    > svg {
      width: 40px;
      height: 40px;
      flex-shrink: 0;
    }

    > div {
      flex: 1;
    }

    h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
    }

    span {
      font-size: 13px;
      opacity: 0.9;
      display: block;
      margin-top: 4px;
    }

    .close-button {
      background: transparent;
      border: none;
      color: white;
      cursor: pointer;
      padding: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
      transition: all 0.2s;

      svg {
        width: 24px;
        height: 24px;
      }

      &:hover {
        background: rgba(255, 255, 255, 0.2);
      }

      &:active {
        transform: scale(0.95);
      }
    }
  }
`;

export const ChatMessages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

export const MessageBubble = styled.div<MessageBubbleProps>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  animation: ${keyframes`
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  `} 0.3s ease-out;
  max-width: 80%;
  align-self: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
  flex-direction: ${({ $role }) => ($role === 'user' ? 'row-reverse' : 'row')};

  .message-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    &.user-avatar {
      background: #428bca;
      color: white;
      padding: 6px;
    }
  }

  .message-content {
    padding: 12px 16px;
    border-radius: 12px;
    background: ${({ $role, $isInitial }) => {
    if ($isInitial) return 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)';
    if ($role === 'user') return '#428bca';
    return '#f1f3f4';
  }};
    color: ${({ $role }) => ($role === 'user' ? 'white' : '#202124')};
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    word-wrap: break-word;
    line-height: 1.5;

    ${({ $isInitial }) => $isInitial && `
      border-left: 4px solid #428bca;
      font-weight: 500;
    `}
  }
`;

const bounce = keyframes`
  0%, 60%, 100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-10px);
  }
`;

export const TypingIndicator = styled.div`
  display: flex;
  gap: 6px;
  padding: 12px 16px;
  background: white;
  border-radius: 16px;
  border-bottom-left-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  span {
    width: 8px;
    height: 8px;
    background: #999;
    border-radius: 50%;
    animation: ${bounce} 1.4s infinite ease-in-out;

    &:nth-child(1) {
      animation-delay: -0.32s;
    }

    &:nth-child(2) {
      animation-delay: -0.16s;
    }
  }
`;

export const ChatInputContainer = styled.div`
  padding: 20px;
  background: white;
  border-top: 1px solid #e0e0e0;
`;

export const InputWrapper = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-end;

  textarea {
    flex: 1;
    padding: 12px;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    font-size: 14px;
    font-family: inherit;
    resize: none;
    transition: border-color 0.2s;

    &:focus {
      outline: none;
      border-color: #428bca;
    }

    &:disabled {
      background: #f5f5f5;
      cursor: not-allowed;
    }
  }

  button {
    min-width: 100px;
    max-width: 120px;
    width: auto !important;
    height: 48px;
    border-radius: 8px;
    font-weight: 600;
    transition: all 0.2s;
    flex-shrink: 0;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(66, 139, 202, 0.3);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;
